import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import * as cssLinter from "css";

export interface ServiceLinkFieldValue {
    type: "InfoLink";
    value: string;
}

interface ServiceCardModel {
    fields: {
        image: string;
        title: string;
        description: string;
        link: ServiceLinkFieldValue;
        linkTitle: string;
        background: string;
        customCSS: string;
    };
}

const enum fields {
    serviceCards = "serviceCards",
    image = "image",
    title = "title",
    description = "description",
    descriptionBackground = "descriptionBackground",
    link = "link",
    linkTitle = "linkTitle",
    serviceCardTitle = "serviceCardTitle",
    background = "background",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.serviceCards]: ServiceCardModel[];
        serviceCardTitle: string;
        background: string;
        descriptionBackground: string;
        customCSS: string;
    };
}

export interface MappedLink {
    title?: string;
    url?: string;
    image?: string;
    description?: string;
    descriptionBackground?: string;
    link?: ServiceLinkFieldValue;
    linkTitle?: string;
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};


const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        fields: { serviceCards },
    } = ownProps;
    const mappedServiceCards = [];
    for (const card of serviceCards) {
        let mappedLink: MappedLink | undefined;
        const readonlyPageLink = getPageLinkByNodeId(state, card.fields.link.value) || undefined;
        const linkData = readonlyPageLink || undefined;
        if (linkData) {
            mappedLink = { ...card.fields };
            mappedLink.url = linkData.url;
        }
        mappedServiceCards.push(mappedLink);
    }
    return {
        mappedServiceCards,
    };
};
export interface ServiceCardsStyles {
    container?: GridContainerProps;
    serviceCard?: GridItemProps;
    customflyImgWrapper?: InjectableCss;
    customDescriptionText?: TypographyPresentationProps;
    customFlyTitle?: TypographyPresentationProps;
    servicecardWrapper?: InjectableCss;
    buttonStyle?: LinkPresentationProps;
    serviceCardTitle?: TypographyPresentationProps;
}

export const serviceCardsStyles: ServiceCardsStyles = {
    serviceCardTitle: {
        variant: "h2",
        css: css`
            text-align: center;
            text-transform: uppercase;
            font-size: 2rem;
            margin: 30px 0px;
            color: ${getColor("primary.main")};
        `,
    },
    container: {
        gap: 38,
        css: css`
            align-items: center;
            margin: 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100% !important;
                            padding: 0 30px;
                        `,
                        css`
                            max-width: 100% !important;
                            padding: 0 30px;
                        `,
                        css`
                            max-width: 720px !important;
                            padding: 0 0px;
                        `,
                        css`
                            max-width: 960px !important;
                            padding: 0 0px;
                        `,
                        css`
                            max-width: 1300px !important;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    serviceCard: {
        width: [12, 6, 6, 6, 6],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 15px;
                        `,
                        css`
                            padding: 15px;
                        `,
                        css`
                            padding: 19px;
                        `,
                        css`
                            padding: 19px;
                        `,
                        css`
                            padding: 19px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    servicecardWrapper: {
        css: css`
            border-bottom: 5px solid ${getColor("primary.main")};
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            padding-bottom: 20px;
            box-shadow: 1px 7px 11px 0px rgb(0 0 0 / 13%);
            background-color: #F8F8F8;
        `,
    },
    customflyImgWrapper: {
        css: css`
            box-sizing: border-box;
            width: 100%;
            height: 180px;
            overflow: hidden;
            div[class^="LazyImageStyle"] {
                width: 100%;
                height: auto;
            }
            img {
                height: 180px;
                width: 100%;
                object-fit: cover;
            }
        `,
    },
    customFlyTitle: {
        variant: "p",
        css: css`
            color: #33333a;
            font-size: 23px;
            font-weight: 600;
            letter-spacing: 0;
            line-height: 30px;
            margin-bottom: 20px;
            padding-left: 20px;
            padding-right: 20px;
            margin-top: 7px;
            text-transform: uppercase;
            text-overflow: ellipsis;
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            max-height: 91px;
            height: 60px;
        `,
    },
    customDescriptionText: {
        variant: "p",
        css: css`
            color: #6C757D;
            font-size: 16px;
            font-weight: 300;
            letter-spacing: 0;
            line-height: 24px;
            margin-bottom: 17px;
            padding-left: 20px;
            padding-right: 20px;
        `,
    },
    buttonStyle: {
        css: css`
            margin-bottom: 24px;
            span {
                background-color: transparent !important;
                color: ${getColor("primary.main")} !important;
                font-size: 14px;
                font-weight: bold;
                letter-spacing: 2px;
                line-height: normal;
                text-transform: uppercase;
                padding: 10px 0px;
                &:hover {
                    text-decoration: underline;
                }
            }
        `,
    },
};

const defaultCustomCss = `.custom-tiles-container {
}

.custom-tiles-card {
}

.custom-tiles-servicecard-wrapper {
}

.custom-tiles-image-wrapper {
}

.custom-tiles-title {
}

.custom-tiles-description {
}

.custom-tiles-link-wrapper {
}

.custom-tiles-link {
}

`;

const styles = serviceCardsStyles;

const DescriptionWrapper = styled.div<{}>`
    height: 100%;
`;

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const CustomTiles: React.FC<Props> = ({ id, fields, mappedServiceCards }) => {
    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper  {...customCssWrapper}>
            {mappedServiceCards?.length > 0 ? (
                <GridContainer {...styles.container} className="custom-tiles-container" id={id}>
                    {mappedServiceCards?.map((card, index) => {
                        return (
                            <GridItem
                                id={`${id}-card-${index}`}
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                className="custom-tiles-card"
                                {...styles.serviceCard}
                            >
                                <DescriptionWrapper>
                                    <StyledWrapper className="custom-tiles-servicecard-wrapper" {...styles.servicecardWrapper}>
                                        {card && card?.image && (
                                            <StyledWrapper {...styles.customflyImgWrapper} className="custom-tiles-image-wrapper">
                                                <LazyImage src={card?.image} altText={card?.title} />
                                            </StyledWrapper>
                                        )}
                                        {card && card?.title && (
                                            <Typography className="custom-tiles-title" {...styles.customFlyTitle}>
                                                {translate(card?.title || "")}
                                            </Typography>
                                        )}
                                        {card && card?.description && (
                                            <Typography className="custom-tiles-description" {...styles.customDescriptionText}>
                                                {card?.description}
                                            </Typography>
                                        )}
                                        {card && card?.url && (
                                            <StyledWrapper
                                                className="custom-tiles-link-wrapper"
                                                css={css`
                                                        padding: 0 20px;
                                                        margin-top: auto;
                                                    `}
                                            >
                                                <Link className="custom-tiles-link" {...styles.buttonStyle} href={card?.url}>
                                                    {translate(card?.linkTitle || "")}
                                                </Link>
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                </DescriptionWrapper>
                            </GridItem>
                        );
                    })}
                </GridContainer>
            ) : null}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CustomTiles),
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.serviceCards,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "Translatable",
                tab: basicTab,
                sortOrder: 2,
                fieldDefinitions: [
                    {
                        name: fields.title,
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 0,
                    },
                    {
                        name: fields.image,
                        displayName: "Photo",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        sortOrder: 1,
                    },
                    {
                        name: fields.description,
                        displayName: "Description",
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                        sortOrder: 6,
                    },

                    {
                        name: fields.link,
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        sortOrder: 4,
                    },

                    {
                        name: fields.linkTitle,
                        displayName: "Link Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 5,
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;